import { AlertTriangle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

import { kindLabel } from '../labels';
import type { AgentTemplate } from '../types';
import { featureGroupForTypes } from '../wizard/draft';
import { stepRegistry } from '../wizard/steps/registry';

export interface TemplateCardProps {
  template: AgentTemplate;
  /** Wizard URL for this template (gallery builds it, projectId passthrough included). */
  to: string;
}

/**
 * Gallery card (design §6.2): the WHOLE card is the click target (role=button,
 * ≥44px content zone, focus-visible ring) — no inner "Use template" button.
 * Hover is a border-hairline emphasis only (no new shadows). The availability
 * badge is computed from the step registry (§6.4), never a hardcoded kind list;
 * the card still navigates when unavailable — the wizard's placeholder explains
 * in context and the review step enforces the stop (B3: disabled-with-reason,
 * not hidden).
 */
export function TemplateCard({ template, to }: TemplateCardProps) {
  const navigate = useNavigate();
  const missingTypes = (template.wizardSteps ?? [])
    .filter((step) => step.required && !stepRegistry.has(step.type))
    .map((step) => step.type);
  const group = featureGroupForTypes(missingTypes);
  const open = () => navigate(to);

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={open}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          open();
        }
      }}
      className="flex min-h-[44px] cursor-pointer flex-col gap-2 rounded-lg border bg-card p-4 transition-colors hover:border-foreground/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 motion-reduce:transition-none"
    >
      <Badge variant="secondary" className="self-start rounded-sm">
        {kindLabel(template.kind)}
      </Badge>
      <p className="text-base font-medium">{template.name}</p>
      {template.description ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <p className="line-clamp-2 text-sm text-muted-foreground">{template.description}</p>
          </TooltipTrigger>
          <TooltipContent className="max-w-xs">{template.description}</TooltipContent>
        </Tooltip>
      ) : null}
      <p className="text-sm text-muted-foreground">
        {(template.wizardSteps?.length ?? 0) + 2} steps
      </p>
      {missingTypes.length > 0 && (
        <Badge
          variant="warning"
          className="mt-auto flex w-fit items-center gap-1 rounded-sm"
          title={`Requires ${group} features`}
        >
          <AlertTriangle className="h-3.5 w-3.5" aria-hidden="true" />
          {`Requires ${group} features`}
        </Badge>
      )}
    </div>
  );
}
